import { assert } from '@fe/core';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Avatar } from '@progress/kendo-react-layout';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { FileSystemExtendedAvailable } from '../../common/helpers';
import { Menu, MenuItem } from '../../components/common/menu/DropdownMenu';
import { ReplayList } from '../../components/replay/PlaybackList';
import {
    AttachImageOutline,
    CameraOutline,
    DownloadOutline,
    EditOutline,
    FeedbackOutline,
    FullscreenOutline,
    LogOutOutline,
    PlayCircleOutline,
    PredictionOutline,
    PredictionSolid,
    StopCircleOutline,
    UploadSolid
} from '../../icons/icons';
import { UDViewType } from '../../udView/udview';
import { useAuthProvider } from '../contexts/AuthProvider';
import { EventBus } from '../contexts/event-bus';
import { ScreenshotCommandType } from '../features/home/layers/MapScreenshot';
import { useAppDispatch, useAppSelector } from '../hooks/store-hooks';
import { ROUTES } from '../router/routes';
import { stopRecording } from '../state/reducers/recording';
import { RootState } from '../state/slices';
import { setSlideshow } from '../state/slices/global-slice';
import { updateView } from '../state/slices/views-slice';
import { WorkspaceStateItem } from '../state/slices/workspace-slice';
import { store } from '../state/store';
import { LoadingIndicatorCircular } from './indicators/LoadingIndicatorCircular';
import CustomPopover from './layout/CustomPopover';
import { StartRecording } from './recording/StartRecording';

export default function ProfileButton() {
    const auth = useAuthProvider();
    const user = useAppSelector((x) => x.user.info);
    const [showStartRecording, setShowStartRecording] = React.useState(false);
    const [showPlayback, setShowPlayback] = React.useState(false);
    const selectedWorkspaceIndex = useAppSelector((x) => x.workspace.selectedId);
    const imageServerUrl = useAppSelector((x) => x.settings.mapImageServerUrl);
    const isFullScreen = useAppSelector((x) => x.global.fullscreen);
    const isSlideshow = useAppSelector((x) => x.global.slideshow);
    const isPlayback = useAppSelector((x) => x.global.playback.active);
    const isRecording = useAppSelector((x) => x.global.recording.active);
    const location = useLocation();
    const queryForViewChanged = useAppSelector((x) => x.settings.queryToSaveViewIfChanged);
    const [logoutActive, setLogoutActive] = React.useState(false);
    const [busy, setBusy] = useState(false);
    const [showViewChangedPopup, setShowViewChangedPopup] = useState<{ views: WorkspaceStateItem[]; shouldLogout: boolean } | null>(null);
    const [errorMessage, setErrorMessage] = useState('');

    const dispatch = useAppDispatch();

    const handleLogout = () => {
        setLogoutActive(true);
        self.setTimeout(() => {
            auth.instance.logout().then();
        }, 100); // prevent screen flash
    };

    const handleSaveViews = async (save: boolean) => {
        assert(showViewChangedPopup);
        setErrorMessage('');
        if (save) {
            setBusy(true);
            for (const workspace of showViewChangedPopup.views) {
                try {
                    await dispatch(updateView({ view: workspace.view, workspaceId: workspace.id })).unwrap();
                } catch {
                    setErrorMessage('Some views could not be saved');
                    setBusy(false);
                    return;
                }
            }
            setBusy(false);
        }

        if (showViewChangedPopup.shouldLogout) {
            handleLogout();
        }
        setShowViewChangedPopup(null);
    };

    useEffect(() => {
        window.onbeforeunload = (e) => {
            if (queryForViewChanged) {
                const modifiedViews = getSavableViews(store.getState());
                if (modifiedViews.length > 0) {
                    e.preventDefault();
                    // Idk if we should show our popup here, probably not
                    //setShowViewChangedPopup({ views: modifiedViews, shouldLogout: false });
                    return 'Pending changes to UD view.';
                    //e.shouldPreventDefault = true;
                }
            }
            return null;
        };
    }, [queryForViewChanged]);

    if (!user.isAuthenticated) {
        return <></>;
    }

    return (
        <>
            {logoutActive && (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.33)', zIndex: 999, backdropFilter: 'blur(2px)' }}>
                    <div className="d-flex flex-column gap-2 align-items-center k-card p-3">
                        <LoadingIndicatorCircular />
                        <div>Logging out</div>
                    </div>
                </div>
            )}

            {showStartRecording && <StartRecording onClose={() => setShowStartRecording(false)} />}
            {showPlayback && (
                <Dialog title="Playback" onClose={() => setShowPlayback(false)}>
                    <ReplayList onClose={() => setShowPlayback(false)} />
                </Dialog>
            )}

            {showViewChangedPopup && (
                <Dialog title="Pending changes" onClose={() => !busy && setShowViewChangedPopup(null)}>
                    <div>You have made changes to the following views that have not yet been saved.</div>
                    <ul>
                        {showViewChangedPopup.views.map((x, i) => (
                            <li key={i}>{x.view.name}</li>
                        ))}
                    </ul>
                    <div>Would you like to save these changes?</div>
                    {errorMessage && <div className="text-danger pt-2">{errorMessage}</div>}
                    <DialogActionsBar>
                        <Button disabled={busy} onClick={() => handleSaveViews(false)}>
                            No
                        </Button>
                        <Button disabled={busy} onClick={() => handleSaveViews(true)} themeColor={'primary'}>
                            Yes
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            <CustomPopover
                trigger={
                    <Button className="k-rounded-full avatar-button" themeColor={'primary'} style={{ width: 27, height: 27 }}>
                        <Avatar type="text" themeColor={'primary'}>
                            <span>{user.name.substring(0, 2).toUpperCase()}</span>
                        </Avatar>
                    </Button>
                }>
                <Menu label="" menuSize={'medium'}>
                    <MenuItem
                        disabled={!FileSystemExtendedAvailable || isPlayback}
                        label={isRecording ? 'Stop recording' : 'Start recording'}
                        icon={isRecording ? StopCircleOutline : CameraOutline} //todo change camera icon to something else
                        onClick={async () => {
                            if (isRecording) {
                                dispatch(stopRecording());
                            } else {
                                setShowStartRecording(true);
                            }
                        }}
                    />

                    <MenuItem
                        disabled={!FileSystemExtendedAvailable || isRecording}
                        label="Playback"
                        icon={PlayCircleOutline}
                        onClick={() => setShowPlayback(true)}
                    />
                    {/*we could let user choose workspaces here or in the settings in the future*/}
                    <Menu
                        disabled={location.pathname !== ROUTES.HOME}
                        label="Save map image"
                        icon={AttachImageOutline}
                        onClick={() => {
                            EventBus.fire('take-map-screenshot', {
                                workspaceIndex: selectedWorkspaceIndex,
                                type: ScreenshotCommandType.save
                            });
                        }}>
                        <MenuItem
                            label="Save"
                            icon={DownloadOutline}
                            onClick={() => {
                                EventBus.fire('take-map-screenshot', {
                                    workspaceIndex: selectedWorkspaceIndex,
                                    type: ScreenshotCommandType.save
                                });
                            }}
                        />
                        <MenuItem
                            label="Save as..."
                            disabled={!('showSaveFilePicker' in window)}
                            icon={EditOutline}
                            onClick={() => {
                                EventBus.fire('take-map-screenshot', {
                                    workspaceIndex: selectedWorkspaceIndex,
                                    type: ScreenshotCommandType.saveAs
                                });
                            }}
                        />
                        <MenuItem
                            label="Upload to server"
                            icon={UploadSolid}
                            disabled={!(imageServerUrl && imageServerUrl.trim().length > 0)}
                            onClick={() => {
                                EventBus.fire('take-map-screenshot', {
                                    workspaceIndex: selectedWorkspaceIndex,
                                    type: ScreenshotCommandType.serverUpload
                                });
                            }}
                        />
                    </Menu>
                    <MenuItem
                        label={isFullScreen ? 'Exit fullscreen' : 'Fullscreen'}
                        icon={FullscreenOutline}
                        onClick={() => EventBus.fire('fullscreen', null)}
                    />
                    <MenuItem
                        label={isSlideshow ? 'Stop slideshow' : 'Start slideshow'}
                        icon={isSlideshow ? PredictionSolid : PredictionOutline}
                        onClick={() => dispatch(setSlideshow({ active: !isSlideshow }))}
                    />
                    <MenuItem label="Feedback" icon={FeedbackOutline} onClick={() => EventBus.fire('open-feedback', null)} />
                    <MenuItem
                        className={'text-error'}
                        label="Logout"
                        icon={LogOutOutline}
                        onClick={() => {
                            if (queryForViewChanged) {
                                const modifiedViews = getSavableViews(store.getState());
                                if (modifiedViews.length > 0) {
                                    setShowViewChangedPopup({ views: modifiedViews, shouldLogout: true });
                                    return;
                                }
                            }
                            handleLogout();
                        }}
                    />
                </Menu>
            </CustomPopover>
        </>
    );
}

function getSavableViews(state: RootState): WorkspaceStateItem[] {
    const modifiedViews = state.workspace.items.filter((x) => x.viewModified);
    const canSaveCompanyViews = state.user.rights.canEditCompanyViews;
    const savableWorkspaces = modifiedViews.filter(
        (x) => x.view.id && (x.view.type === UDViewType.user || (x.view.type === UDViewType.company && canSaveCompanyViews))
    );

    const withoutDuplicates = savableWorkspaces.reduce(
        (p, n) => (p.some((x) => x.view.id === n.view.id) ? p : [...p, n]),
        [] as WorkspaceStateItem[]
    );
    return withoutDuplicates;
}
